import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLayoutEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Send, Copy, RotateCcw, User, Bot, Loader2, MessageSquare, Plus, Clock, Search } from 'lucide-react';
import '../styles/modernHuxley.css';

function Huxley() {
  const { user, isAuthenticated, refreshToken } = useAuth();
  
  // Chat state
  const [chat, setChat] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [imageModal, setImageModal] = useState({ show: false, url: '' });
  const [showHistory, setShowHistory] = useState(false);
  const [currentChatId, setCurrentChatId] = useState(null);
  
  // Chat history - you can load this from localStorage or your backend
  const [chatHistory, setChatHistory] = useState(() => {
    const saved = localStorage.getItem('jason-chat-history');
    return saved ? JSON.parse(saved) : [];
  });
  
  const chatWindowRef = useRef(null);
  const textareaRef = useRef(null);

  // Your existing formatMessage function
const formatMessage = useCallback((md) => {
  // Convert escaped newlines to actual newlines
  md = md.replace(/\\n/g, '\n');
    
  const blocks = md.trim().split(/\n{2,}/);
  const html = blocks.map(block => {
    if (/^###\s/.test(block)) return `<h3>${block.slice(4)}</h3>`;
    if (/^##\s/.test(block))  return `<h2>${block.slice(3)}</h2>`;
    if (/^#\s/.test(block))   return `<h1>${block.slice(2)}</h1>`;

    // Handle numbered lists - ADD THIS BLOCK
    if (/^\d+\.\s/.test(block)) {
      const items = block
        .split('\n')
        .filter(line => line.trim()) // Remove empty lines
        .map(line => {
          const content = line.replace(/^\d+\.\s/, '');
          return `<li>${content}</li>`;
        })
        .join('');
      return `<ol>${items}</ol>`;
    }

    // Your existing bullet list code stays the same
    if (/^- /.test(block)) {
      const items = block
        .split('\n')
        .map(line => `<li>${line.replace(/^- /, '')}</li>`)
        .join('');
      return `<ul>${items}</ul>`;
    }

    // All your existing formatting stays exactly the same
    const inner = block
      .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="chart-image" onclick="openImageModal(\'$2\')" />')
      .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
      .replace(/(\w+)\[(https?:\/\/[^\]]+)\]/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br />');

    return `<p>${inner}</p>`;
  }).join('');

  return html.replace(/^<p><\/p>/, '');
},[]);

  // Your existing getWelcomeMessage function (without refreshToken call)
  const getWelcomeMessage = useCallback(async () => {
    try {
      const result = await fetch('https://n8n.iniai.cloud/webhook/jason_v2', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'welcome',
          user: {
            id: user?.id || '',
            email: user?.email || '',
            role: user?.role || '',
            name: user?.name || ''
          },
          sessionId: `${user?.id || 'anonymous'}-${Date.now()}`,
          context: {
            isAuthenticated,
            timestamp: new Date().toISOString()
          }
        })
      });

      const data = await result.json();

      if (Array.isArray(data) && data.length > 0) {
        const response = data[0].output || data[0].response;
        if (response) {
          setChat([{ 
            id: Date.now(),
            type: 'jason', 
            content: formatMessage(response),
            timestamp: new Date()
          }]);
        }
      }
    } catch (error) {
      console.error('Error fetching welcome message:', error);
      setChat([{
        id: Date.now(),
        type: 'jason',
        content: `Welcome to Huxlon AI. How can I assist you today?`,
        timestamp: new Date()
      }]);
    }
  }, [user, isAuthenticated, formatMessage]);

  // Load welcome message on mount
  useEffect(() => {
    if (isAuthenticated && user && chat.length === 0) {
      getWelcomeMessage();
    }
  }, [isAuthenticated, user, chat.length, getWelcomeMessage]);

  // Auto-scroll to bottom
  useLayoutEffect(() => {
    if (chatWindowRef.current) {
      requestAnimationFrame(() => {
        chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
      });
    }
  }, [chat]);

  // Save chat history to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('jason-chat-history', JSON.stringify(chatHistory));
  }, [chatHistory]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!query.trim() || loading) return;
  
    const userMessage = { 
      id: Date.now(),
      type: 'user', 
      content: query.trim(),
      timestamp: new Date()
    };
    
    setChat(prevChat => [...prevChat, userMessage]);
    const currentQuery = query;
    setQuery('');
    setLoading(true);
  
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, 600000);
  
    try {
      const result = await fetch('https://n8n.iniai.cloud/webhook/jason_v2', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
        },
        signal: controller.signal,
        body: JSON.stringify({
          type: 'query',
          query: currentQuery,
          user: {
            id: user?.id || '',
            email: user?.email || '',
            role: user?.role || '',
            name: user?.name || ''
          },
          sessionId: `${user?.id || 'anonymous'}-${Date.now()}`,
          context: {
            isAuthenticated,
            timestamp: new Date().toISOString()
          }
        })
      });

      clearTimeout(timeoutId);
      const data = await result.json();
  
      if (Array.isArray(data) && data.length > 0) {
        const formattedContent = formatMessage(data[0].response || data[0].output);
        const assistantMessage = { 
          id: Date.now() + 1,
          type: 'jason', 
          content: formattedContent,
          timestamp: new Date()
        };
        setChat(prevChat => [...prevChat, assistantMessage]);
        
        // Save this conversation to history
        saveChatToHistory([...chat, userMessage, assistantMessage]);
      }
    } catch (error) {
      clearTimeout(timeoutId);
      
      const errorMessage = error.name === 'AbortError' || error.code === 'ECONNABORTED'
        ? 'This query is taking longer than expected. Try simplifying your question.'
        : error.response?.data?.message || 'An error occurred while processing your request.';
      
      setChat(prevChat => [...prevChat, { 
        id: Date.now() + 1,
        type: 'jason', 
        content: errorMessage,
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleTextareaChange = (e) => {
    setQuery(e.target.value);
    
    // Auto-resize textarea
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    }
  };

  // Add this function before your return statement
const openImageModal = useCallback((imageUrl) => {
  setImageModal({ show: true, url: imageUrl });
}, []);

const closeImageModal = () => {
  setImageModal({ show: false, url: '' });
};

// Make the function globally available for the onclick
useEffect(() => {
  window.openImageModal = openImageModal;
  return () => {
    delete window.openImageModal;
  };
}, [openImageModal]);
  const copyToClipboard = useCallback((text) => {
    const cleanText = text.replace(/<[^>]*>?/gm, '');
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(cleanText)
        .then(() => alert('Copied to clipboard!'))
        .catch((err) => {
          console.error('Failed to copy: ', err);
          alert('Failed to copy to clipboard.');
        });
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = cleanText;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand('copy');
        alert('Copied to clipboard!');
      } catch (err) {
        console.error('Fallback: Oops, unable to copy', err);
        alert('Failed to copy to clipboard.');
      }
      document.body.removeChild(textarea);
    }
  }, []);

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    return 'Just now';
  };

  const saveChatToHistory = (messages) => {
    if (messages.length < 2) return; // Don't save if just welcome message
    
    const chatTitle = messages.find(m => m.type === 'user')?.content.slice(0, 50) + '...' || 'New Chat';
    const newChat = {
      id: `chat-${Date.now()}`,
      title: chatTitle,
      lastMessage: messages[messages.length - 1]?.content.replace(/<[^>]*>?/gm, '').slice(0, 100) + '...',
      timestamp: new Date(),
      messageCount: messages.length,
      messages: messages
    };
    
    setChatHistory(prev => [newChat, ...prev.slice(0, 19)]); // Keep only last 20 chats
  };

  const startNewChat = () => {
    setChat([]);
    setCurrentChatId(null);
    setShowHistory(false);
    getWelcomeMessage();
  };

  const loadChat = (chatId) => {
    const savedChat = chatHistory.find(c => c.id === chatId);
    if (savedChat) {
      setChat(savedChat.messages);
      setCurrentChatId(chatId);
      setShowHistory(false);
    }
  };

  return (
    
    <div className="modern-chat-container">
      {/* Chat History Sidebar */}
      <div className={`chat-sidebar ${!showHistory ? 'hidden' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-title">
            <h2>Chat History</h2>
            <button
              onClick={() => setShowHistory(false)}
              className="close-sidebar-btn"
            >
              ×
            </button>
          </div>
          <div className="search-box">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Search chats..."
              className="search-input"
            />
          </div>
        </div>
        
        <div className="chat-history-list">
          {chatHistory.map((savedChat) => (
            <div
              key={savedChat.id}
              onClick={() => loadChat(savedChat.id)}
              className={`chat-history-item ${currentChatId === savedChat.id ? 'active' : ''}`}
            >
              <h3 className="chat-title">{savedChat.title}</h3>
              <p className="chat-preview">{savedChat.lastMessage}</p>
              <div className="chat-meta">
                <Clock size={12} />
                <span>{formatTimeAgo(savedChat.timestamp)}</span>
                <span>•</span>
                <span>{savedChat.messageCount} messages</span>
              </div>
            </div>
          ))}
          
          {chatHistory.length === 0 && (
            <div className="empty-history">
              <MessageSquare className="empty-history-icon" />
              <p>No chat history yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="main-chat-area">
        {/* Header */}
        <div className="chat-header">
          <div className="header-content">
            <div className="agent-info">
              <div className="agent-avatar">
                <Bot size={20} />
              </div>
              <div className="agent-details">
                <h1>Jason</h1>
                <p>Strategy AI Agent</p>
              </div>
            </div>
            
            <div className="header-controls">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="header-btn"
                title="Chat History"
              >
                <MessageSquare size={20} />
              </button>
              <button
                onClick={startNewChat}
                className="new-chat-btn"
              >
                <Plus size={16} />
                New Chat
              </button>
            </div>
          </div>
        </div>

        {/* Messages Container */}
        <div className="messages-container" ref={chatWindowRef}>
          <div className="messages-wrapper">
            {chat.map((message) => (
              <div key={message.id} className={`message-group ${message.type}`}>
                <div className={`message-avatar ${message.type === 'jason' ? 'assistant' : 'user'}`}>
                  {message.type === 'jason' ? <Bot size={20} /> : <User size={20} />}
                </div>
                
                <div className={`message-content-wrapper ${message.type}`}>
                  <div className={`message-bubble ${message.type === 'jason' ? 'assistant' : 'user'}`}>
                    <div 
                      className={`message-text ${message.type}`}
                      dangerouslySetInnerHTML={{ __html: message.content }}
                    />
                  </div>
                  
                  {/* Message Actions - Only for Jason messages */}
                  {message.type === 'jason' && (
                    <div className="message-actions">
                      <button
                        onClick={() => copyToClipboard(message.content)}
                        className="action-btn"
                        title="Copy message"
                      >
                        <Copy size={16} />
                      </button>
                      <button
                        className="action-btn"
                        title="Regenerate response"
                      >
                        <RotateCcw size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Typing Indicator */}
            {loading && (
              <div className="typing-indicator">
                <div className="message-avatar assistant">
                  <Bot size={20} />
                </div>
                <div className="typing-content">
                  <div className="typing-text">
                    <Loader2 className="spinner" size={16} />
                    <span>Jason is consulting with the team...</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Input Area */}
        <div className="input-area">
          <div className="input-wrapper">
            <div className="input-container">
              <textarea
                ref={textareaRef}
                value={query}
                onChange={handleTextareaChange}
                onKeyDown={handleKeyDown}
                placeholder="Message Jason..."
                className="message-input"
                rows={1}
                disabled={loading}
              />
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!query.trim() || loading}
                className="send-btn"
              >
                <Send size={16} />
              </button>
            </div>
            
            <div className="input-hint">
              <p>Press Enter to send, Shift+Enter for new line</p>
            </div>
          </div>
        </div>
      </div>
            {/* Image Modal */}
      {imageModal.show && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={closeImageModal}>×</button>
            <img src={imageModal.url} alt="Chart full size" className="image-modal-image" />
          </div>
        </div>
      )}
    </div>
  );
}

export default Huxley;